import { CircularProgress, Snackbar } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import {
  useAbundanceEngineGetViewPathDetails,
  useGetViewVersion,
  useModifyViewVersion,
  useSetLiveViewVersion
} from '../../../../../api/abundanceEngine'
import { createAbundanceEngineView, editAbundanceEngineView } from '../../../../../service'
import Alert from '../../../../atoms/Alert'
import SydButton from '../../../../commonDesign/Button'
import SydLinkButton from '../../../../commonDesign/LinkButton'
import ViewContentContainer from './ViewContentContainer'
import ViewDetailInputs from './ViewDetailInputs'
import WorkingVersionBar from './WorkingVersionBar'

function AbundanceEngineEditView () {
  const history = useHistory()
  const params = useParams()
  const isEdit = useMemo(() => params.path?.length > 0, [params.path])
  const [alert, setAlert] = useState(null)
  const contentContainerRef = useRef(null)
  const [initialContent, setInitialContent] = useState('')
  const [workingVersionId, setWorkingVersionId] = useState(null)
  const [versionDescription, setVersionDescription] = useState('')
  const [isSavingView, setIsSavingView] = useState(false)

  const form = useForm({
    defaultValues: {
      path: '',
      name: '',
      version: 1,
      description: '',
      content: '',
      isLive: false,
      hideTitle: false,
      disabledViewId: null,
      enablementCodeId: null,
      liveViewVersionId: null
    }
  })

  const { data, isFetching } = useAbundanceEngineGetViewPathDetails({ path: params.path }, {
    enabled: isEdit && !!params.path?.length
  })

  useEffect(() => {
    if (isEdit && data) {
      form.reset({
        ...data,
        showInNavigation: !!data.showInNavigation,
        isLive: !!data.isLive,
        hideTitle: !!data.hideTitle
      })
      setInitialContent(data.content || '')
      if (data.liveViewVersionId && !workingVersionId) {
        setWorkingVersionId(data.liveViewVersionId)
      }
    }
  }, [data, form, isEdit, workingVersionId])

  const { data: versionData, isFetching: isFetchingVersion } = useGetViewVersion(workingVersionId)

  useEffect(() => {
    if (versionData) {
      if (workingVersionId && versionData.viewVersionId && versionData.viewVersionId !== workingVersionId) {
        return
      }
      if (versionData.content != null) {
        setInitialContent(versionData.content)
        form.setValue('content', versionData.content)
      }
      setVersionDescription(versionData.description || '')
    }
  }, [versionData, form, workingVersionId])

  const modifyVersion = useModifyViewVersion()

  const handleSaveVersion = useCallback(async () => {
    if (!workingVersionId) return
    try {
      const latestContent = contentContainerRef.current?.getEditorValue?.() || initialContent
      await modifyVersion.mutateAsync({
        viewVersionId: workingVersionId,
        content: latestContent,
        description: versionDescription || null
      })
      setAlert({ severity: 'success', message: 'Version saved' })
    } catch (err) {
      console.error(err)
      setAlert({ severity: 'error', message: err?.message || 'Failed to save version' })
    }
  }, [workingVersionId, initialContent, versionDescription, modifyVersion])

  const setLiveVersion = useSetLiveViewVersion()

  const handleSaveView = useCallback(async () => {
    const formData = form.getValues()
    if (!formData?.viewId) return
    setIsSavingView(true)
    try {
      const viewMetadata = { ...formData }
      delete viewMetadata.content
      await editAbundanceEngineView(formData.viewId, viewMetadata)

      if (formData.liveViewVersionId !== data?.liveViewVersionId) {
        await setLiveVersion.mutateAsync({
          viewId: formData.viewId,
          viewVersionId: formData.liveViewVersionId
        })
      }

      if (formData.path !== params.path) {
        history.replace(`/admin/abundance-engine-views/edit/${formData.path}`)
      }
      setAlert({ severity: 'success', message: 'View saved' })
    } catch (err) {
      console.error(err)
      setAlert({ severity: 'error', message: err?.message || 'Failed to save view' })
    } finally {
      setIsSavingView(false)
    }
  }, [form, data, params.path, history, setLiveVersion])

  const handleCreateView = useCallback(async () => {
    const formData = form.getValues()
    setIsSavingView(true)
    try {
      const latestContent = contentContainerRef.current?.getEditorValue?.() || formData.content
      const { data } = await createAbundanceEngineView({ ...formData, content: latestContent })
      history.push(`/admin/abundance-engine-views/edit/${data.path}`)
      setAlert({ severity: 'success', message: 'View created successfully' })
    } catch (err) {
      console.error(err)
      setAlert({ severity: 'error', message: err?.message || 'An error occurred' })
    } finally {
      setIsSavingView(false)
    }
  }, [form, history])

  const viewId = form.watch('viewId')
  const isProcessing = isFetching || isFetchingVersion || modifyVersion.isPending || isSavingView

  const snackbar = (
    <Snackbar
      open={!!alert}
      autoHideDuration={6000}
      onClose={() => setAlert(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      {!!alert && (
        <Alert onClose={() => setAlert(null)} severity={alert?.severity}>
          {alert?.message}
        </Alert>
      )}
    </Snackbar>
  )

  // ── Edit mode ──
  return (
    <>
      {snackbar}
      <FormProvider {...form}>
        <div
          className='h-full flex flex-col relative'
          style={{ maxWidth: 'calc(100vw - 50px)', ...(isProcessing && { opacity: 0.5, pointerEvents: 'none' }) }}
        >
          {isProcessing && (
            <div
              className='absolute'
              style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}
            >
              <CircularProgress />
            </div>
          )}

          <div className='flex gap-5 p-4 px-5 w-full h-full'>
            {/* Left panel – View Settings */}
            <div className='flex flex-col gap-5' style={{ width: '320px' }}>
              {isEdit ? (
                <div>
                  <p className='m-0 font-bold font-size-lg'>View Settings</p>
                  <p className='m-0 font-light font-size-sm'>
                    Edit the details of this view.
                  </p>
                </div>
              ) : (
                <div>
                  <p className='m-0 font-bold font-size-lg'>Create New View</p>
                  <p className='m-0 font-light font-size-sm'>
                    Set up the details for your new view.
                  </p>
                </div>
              )}
              <div className='flex-1 flex flex-col gap-4' style={{ overflow: 'auto' }}>
                <ViewDetailInputs onAlert={setAlert} />
              </div>
              <div className='flex justify-between'>
                <SydButton
                  variant='secondary'
                  type='button'
                  onClick={() => {
                    if (form.formState.isDirty) {
                      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                        history.push('/admin/abundance-engine-views')
                      }
                    } else {
                      history.push('/admin/abundance-engine-views')
                    }
                  }}
                >
                  Cancel
                </SydButton>
                <SydButton
                  type='button'
                  onClick={isEdit ? handleSaveView : handleCreateView}
                  processing={isSavingView}
                  disabled={isSavingView}
                >
                  Save View Settings
                </SydButton>
              </div>
            </div>

            {/* Right panel – Version Content */}
            <div className='flex-1 flex flex-col' style={{ overflow: 'hidden', minWidth: 0 }}>
              <div className='flex-1' style={{ overflow: 'hidden' }}>
                <ViewContentContainer
                  ref={contentContainerRef}
                  jsxContent={initialContent}
                  viewId={viewId}
                  onJsxContentChange={(jsx) => {
                    form.setValue('content', jsx, { shouldDirty: true })
                    setInitialContent(jsx)
                  }}
                  toolbarLeft={
                    <WorkingVersionBar
                      viewId={viewId}
                      workingVersionId={workingVersionId}
                      onWorkingVersionChange={setWorkingVersionId}
                      versionDescription={versionDescription}
                      onVersionDescriptionChange={setVersionDescription}
                      getCurrentContent={() => contentContainerRef.current?.getEditorValue?.() || initialContent}
                      onAlert={setAlert}
                    />
                  }
                />
              </div>
              <div className='flex items-center justify-between pt-3'>
                {isEdit && (
                  <>
                    <div style={{ marginLeft: 2 }}>
                      {workingVersionId && (
                        <SydLinkButton
                          href={`/views/${params.path}/?viewVersionId=${workingVersionId}`}
                          target='_blank'
                          icon='visibility'
                          size='sm'
                        >
                          Go to Version View
                        </SydLinkButton>
                      )}
                    </div>

                    <SydButton
                      type='button'
                      variant='primary'
                      icon='check'
                      onClick={handleSaveVersion}
                      processing={modifyVersion.isPending}
                      disabled={modifyVersion.isPending || !workingVersionId}
                    >
                      Save Version
                    </SydButton>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </FormProvider>
    </>
  )
}

export default AbundanceEngineEditView
